import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { LayoutService } from '@app/shared/components/layout/layout.service';
import { constants } from '@app/shared/utils/constants';
import { ToastService } from '@teorema/patron';
import { Subscription } from 'rxjs';
import {
  FilterField,
  FilterOperator,
  FindOptions,
  Task,
  Ticket,
  TicketTask,
  User,
} from 'src/app/models';
import { RealtimeService } from 'src/app/services/realtime.service';
import { TicketNotificationsService } from 'src/app/services/ticket-notifications.service';
import { utils } from 'src/app/shared';
import { SortPipe } from 'src/app/shared/pipes';
import { SprintBoardState, SprintStateService } from '../services/sprint-state.service';
import {
  AgentTicketService,
  TcCurrentLogService,
  TcTicketLogService,
  TcTicketService,
  UserService,
  UtilsService,
} from 'src/app/shared/services';

@Component({
  selector: 'app-abstract-sprint',
  template: '',
  standalone: false,
})
export class AbstractSprintComponent implements OnInit, OnDestroy {
  loading = false;
  tickets: Ticket[] = [];
  ticketTasks: TicketTask[] = [];
  filteredTickets: TicketTask[] = [];
  companyGroupId?: string;
  filterUserChanged: boolean = false;
  lastFilteredResponsible: any[] = [];
  lastFilteredSituation: any;

  isAgentTicket: boolean = false;

  showCurrentTicketModal: boolean = false;
  openingTicketModal: boolean = false;
  currentTicket?: Ticket;
  currentTaskId?: string;
  subscriptions: Subscription[] = [];
  logsLoaded = false;
  ticketsSituation = constants.ticketcenter.ticketsSituation;

  sprints: Record<string, any> = {};
  notPunctuatedSprint: Record<string, any> = {};
  punctuatedSprint: Record<string, any> = {};
  delayedAndFinishedSprint: Record<string, any> = {};
  delayedSprint: Record<string, any> = {};
  sprintOpenedUser: Record<string, boolean> = {};
  screenHeight: number = 0;
  minimizedSprints: Record<string, any> = {};

  protected sprintStateService = inject(SprintStateService);
  public readonly sprintState$ = this.sprintStateService.state$;

  findOptions: FindOptions = {
    storage: 'local',
    perPage: 1000,
    //orderBy: 'name',
    storageKey: 'sprint-find-options',
    //withs: ['tasks'],
    q: '',
    filters: [
      new FilterField({
        name: 'situation',
        label: 'Situação',
        type: 'select',
        options: this.ticketsSituation,
        operator: FilterOperator.equal,
        bindValue: 'value',
        bindLabel: 'label',
      }),
      new FilterField({
        name: 'responsible',
        label: 'Responsável',
        type: 'select',
        multiple: true,
        modelName: 'users',
        orderBy: 'name',
        bindValue: 'id',
        bindLabel: 'name',
        value: [],
      }),
      new FilterField({
        name: 'expectedDate',
        label: 'Data',
        type: 'betweenDates',
        operator: FilterOperator.inner,
      }),
      new FilterField({
        name: 'number',
        label: 'Número',
        type: 'select',
        multiple: true,
        options: [],
        operator: FilterOperator.inner,
        bindValue: 'value',
        bindLabel: 'value',
      }),
      new FilterField({
        name: 'ticketDev',
        label: 'Desenvolvedor',
        type: 'select',
        multiple: true,
        options: [],
        operator: FilterOperator.inner,
        bindValue: 'id',
        bindLabel: 'name',
      }),
      new FilterField({
        name: 'system',
        label: 'Sistema',
        type: 'select',
        multiple: true,
        options: [],
        operator: FilterOperator.inner,
        bindValue: 'id',
        bindLabel: 'description',
      }),
      new FilterField({
        name: 'status',
        label: 'Status',
        type: 'select',
        multiple: true,
        options: [],
        operator: FilterOperator.inner,
        bindValue: 'id',
        bindLabel: 'description',
      }),
      new FilterField({
        name: 'customer',
        label: 'Cliente',
        type: 'select',
        multiple: true,
        options: [],
        operator: FilterOperator.inner,
        bindValue: 'id',
        bindLabel: 'name',
      }),
      new FilterField({
        name: 'tags',
        label: 'Tags',
        type: 'select',
        multiple: true,
        options: [],
        operator: FilterOperator.inner,
        bindValue: 'id',
        bindLabel: 'name',
      }),
    ],
  };

  private socketService = inject(RealtimeService);
  private changeDetector = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  protected patchState(partial: Partial<SprintBoardState>) {
    this.sprintStateService.update(partial);
    this.changeDetector.markForCheck();
  }

  protected emitBoardState(extra?: Partial<SprintBoardState>) {
    const base: Partial<SprintBoardState> = {
      tickets: [...this.tickets],
      ticketTasks: [...this.ticketTasks],
      filteredTickets: [...this.filteredTickets],
      sprints: { ...this.sprints },
      notPunctuatedSprint: { ...this.notPunctuatedSprint },
      punctuatedSprint: { ...this.punctuatedSprint },
      delayedAndFinishedSprint: { ...this.delayedAndFinishedSprint },
      delayedSprint: { ...this.delayedSprint },
      minimizedSprints: { ...this.minimizedSprints },
      sprintOpenedUser: { ...this.sprintOpenedUser },
      loading: this.loading,
    };

    this.patchState({ ...base, ...extra });
  }

  public syncBoardState(extra?: Partial<SprintBoardState>) {
    this.emitBoardState(extra);
  }

  public setLoading(value: boolean) {
    this.loading = value;
    this.patchState({ loading: value });
  }

  constructor(
    protected tcTicketService: TcTicketService,
    protected toastService: ToastService,
    protected datePipe: DatePipe,
    protected sortPipe: SortPipe,
    protected layoutService: LayoutService,
    protected userService: UserService,
    protected utilsService: UtilsService,
    protected tcTicketLogService: TcTicketLogService,
    protected currentTicketLogService: TcCurrentLogService,
    protected agentTicketService: AgentTicketService,
    protected ticketNotificationsService: TicketNotificationsService
  ) {
    this.companyGroupId = utils.getCompanyGroup()?.id;
    this.layoutService.setPadding('bottom', '0');
    this.layoutService.setBlockContentStyle('overflow-x', 'hidden !important');
    //this.layoutService.setDimensions('height', undefined);
  }

  async ngOnInit() {
    await this.initializeFilters();

    this.getTickets();
    this.updateNotificationTitleOnInit();

    this.subscriptions.push(
      this.currentTicketLogService.startedTicketLogEmitter.subscribe(log => {
        if (log?.user?.name) {
          this.ticketTasks.forEach(ticketTask => {
            if (ticketTask.ticket?.id == log.ticket_id) {
              if (log.task && log.task.id === ticketTask.task?.id) {
                this.addUserLogToTicket(ticketTask.task, log.user!.name!);
              }

              this.addUserLogToTicket(ticketTask.ticket, log.user!.name!);
            }
          });
        }
      })
    );

    this.subscriptions.push(
      this.currentTicketLogService.stoppedTicketLogEmitter.subscribe(log => {
        if (log?.user?.name) {
          this.ticketTasks.forEach(ticketTask => {
            if (ticketTask.ticket?.id == log.ticket_id) {
              if (log.task && log.task.id === ticketTask.task?.id) {
                this.removeUserLogFromTicket(ticketTask.task, log.user!.name!);
              }

              this.removeUserLogFromTicket(ticketTask.ticket, log.user!.name!);
            }
          });
        }
      })
    );

    this.watchTicketsChanges();
  }

  watchTicketsChanges() {
    // Canal 'sprint': usado apenas para atualizar a UI do board. A inscricao entra em
    // this.subscriptions, que o ngOnDestroy cancela - e isso que tira a tela do canal.
    this.subscriptions.push(this.socketService.subscribeToChannel('sprint').subscribe((resp: any) => {
      const obj = JSON.parse(resp.message);
      const ticketId = obj.ticket_id;
      if (ticketId) {
        const index = this.tickets.findIndex(t => t.id == ticketId);
        if (index >= 0) {
          const ticket = this.tickets[index];
          this.tcTicketService.compare(ticket).then((response: any) => {
            const updatedTicket = { ...ticket, ...response.ticket_basic };
            if (updatedTicket.situation == 'A') {
              this.tickets[index] = updatedTicket;
            } else {
              this.tickets.splice(index, 1);
            }
            this.tickets = [...this.tickets];
            this.populateTasks();
            this.adjustTicketUsersList();
            this.filterTickets();
          });
        }
      }
    }));
    // Nota: notificações push do canal pessoal UUID são processadas globalmente no AppComponent
  }

  async initializeFilters() {
    this.findOptions = this.utilsService.restoreFindOptions(this.findOptions);

    let responsibleFilter = this.findOptions.filters?.find(f => f.name === 'responsible');

    if (responsibleFilter && !responsibleFilter.value?.length) {
      let userId =
        this.companyGroupId === '5e7c3ea1-bbf8-4254-8d7d-8b730d7548ac'
          ? '7dbb1b47-c35d-450c-97e7-ca5f3e45fb95'
          : 'ab3abd99-fbd9-46cb-9765-a806852547d1';

      let response = await this.userService.findById(userId).catch(e => {
        console.error(e);
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao carregar os dados',
        });
      });

      if (response?.data?.id) {
        responsibleFilter.value = [response?.data];
      }

      if (this.companyGroupId === 'd354b40b-c56f-46a4-b033-33fcebdbac17') {
        response = await this.userService
          .findById('bd711823-6f8e-4455-8ef4-fab8a552066e')
          .catch(e => {
            console.error(e);
            this.toastService.show({
              color: 'error',
              message: 'Houve um erro ao carregar os dados',
            });
          });

        if (response?.data?.id) {
          responsibleFilter.value.push(response.data);
        }
      }
    }
    this.findOptions = { ...this.findOptions }; // Force OnPush update
    this.changeDetector.markForCheck();
  }

  async getTickets(findOptions?: FindOptions) {
    if (this.loading) return;
    if (findOptions) this.findOptions = findOptions;

    let users = this.findOptions.filters?.find(f => f.name === 'responsible')?.value;

    if (!users || !users.length) {
      this.toastService.show({
        color: 'error',
        message: 'Selecione os usuários responsáveis',
      });
      return;
    }

    this.setSituationFilter();

    this.saveResponsible(users);
    this.lastFilteredResponsible = JSON.parse(JSON.stringify(users)); // Deep copy to avoid reference issues
    let situation = this.findOptions.filters?.find(f => f.name === 'situation')?.value;
    this.lastFilteredSituation = situation;

    this.filterUserChanged = false;
    this.setLoading(true);
    let response;

    try {
      if (this.isAgentTicket) {
        response = await this.agentTicketService.findAll(this.findOptions).catch(e => {
          console.error(e);
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao carregar os dados',
          });
        });
      } else {
        response = await this.tcTicketService.findAllBasic(this.findOptions).catch(e => {
          console.error(e);
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao carregar os dados',
          });
        });
      }
    } finally {
      this.setLoading(false);
    }

    if (response?.data?.length) {
      this.tickets = response.data;
    } else {
      this.tickets = [];
      this.toastService.show({
        color: 'warn',
        message: 'Nenhum chamado encontrado',
      });
    }

    this.populateTasks();
    this.adjustTicketUsersList();
    this.filterTickets();
  }

  setSituationFilter() {
    let situationFilter = this.findOptions.filters!.find(f => f.name === 'situation');

    if (!situationFilter!.value) {
      situationFilter!.value = this.ticketsSituation.find(ts => ts.value === 'A');
    }
  }

  populateTasks(id?) {
    this.ticketTasks = [];

    this.adjustTicket(this.tickets);

    this.ticketTasks = this.sortPipe.transform(this.ticketTasks, [
      'task.created_at',
      'ticket.original_expected_date',
      'ticket.expected_date',
      'task.view_index',
    ]);

    this.findTicketLogs(id);
  }

  adjustTicket(ticket: Ticket[]) {
    ticket.forEach(ticket => {
      /*if (ticket.ticket_number_teorema === '0000489089') {
        console.log(ticket);
        debugger;
      }*/

      if (ticket.tasks && ticket.tasks.length) {
        ticket.tasks.forEach(task => {
          this.ticketTasks.push({
            ticket: ticket,
            task: task,
          });
        });
      } else {
        this.ticketTasks.push({
          ticket: ticket,
          task: this.setTaskPropertiesFromTicket(new Task(), ticket),
        });
      }
    });
  }

  setTaskPropertiesFromTicket(task, ticket) {
    if (ticket.estimated_effort == undefined) {
      ticket.estimated_effort = null;
    }
    task.estimated_effort = ticket.estimated_effort;
    task.description = ticket.summary || ticket.problem;
    task.expected_date = ticket.expected_date;
    task.status = ticket.status;
    task.view_index = ticket.view_index;

    let dev = ticket.developer;

    if (!dev?.id) {
      dev = ticket.ticketUsers?.find(tu => tu.user && tu.user_type == 'D')?.user;
    }

    if (dev) {
      task.user = dev;
    }

    return task;
  }

  adjustTicketUsersList(id?) {
    let numberFilter = this.findOptions.filters?.find(f => f.name === 'number');
    let systemFilter = this.findOptions.filters?.find(f => f.name === 'system');
    let statusFilter = this.findOptions.filters?.find(f => f.name === 'status');
    let customersFilter = this.findOptions.filters?.find(f => f.name === 'customer');
    let ticketDevFilter = this.findOptions.filters?.find(f => f.name === 'ticketDev');
    let tagsFilter = this.findOptions.filters?.find(f => f.name === 'tags');

    let numbers: any[] = [];
    let devs: any[] = [];
    let systems: any[] = [];
    let status: any[] = [];
    let customers: any[] = [];
    let tags: any[] = [];

    if (id) {
      numbers = numberFilter?.options || [];
      systems = systemFilter?.options || [];
      status = statusFilter?.options || [];
      customers = customersFilter?.options || [];
      devs = ticketDevFilter?.options || [];
      tags = tagsFilter?.options || [];
    }

    this.ticketTasks.map(entity => {
      if (!id || entity.ticket?.id === id) {
        if (entity.ticket?.system && entity.ticket.system.description) {
          if (!systems.find(s => s.id === entity.ticket?.system?.id)) {
            systems.push({
              id: entity.ticket.system.id,
              description: entity.ticket.system.description,
            });
          }
        }

        if (entity.task?.systems?.length) {
          entity.task?.systems.forEach(taskSystem => {
            if (taskSystem.description && !systems.find(s => s.id === taskSystem.id)) {
              systems.push({
                id: taskSystem.id,
                description: taskSystem.description,
              });
            }
          });
        }

        if (entity.ticket && entity.ticket.ticket_number_teorema) {
          if (!numbers.find(n => n.value === entity.ticket?.ticket_number_teorema)) {
            numbers.push({
              value: entity.ticket.ticket_number_teorema,
            });
          }
        }

        if (entity.task?.status && entity.task?.status?.description) {
          if (!status.find(s => s.id === entity.task?.status?.id)) {
            status.push({
              id: entity.task?.status.id,
              description: entity.task?.status.description,
            });
          }
        }

        if (entity.ticket?.status && entity.ticket?.status.description) {
          if (!status.find(s => s.id === entity.ticket?.status?.id)) {
            status.push({
              id: entity.ticket.status.id,
              description: entity.ticket.status.description,
            });
          }
        }

        if (entity.ticket?.customer && entity.ticket.customer.name) {
          if (!customers.find(s => s.id === entity.ticket?.customer?.id)) {
            customers.push({
              id: entity.ticket.customer.id,
              name: entity.ticket.customer.name,
            });
          }
        }

        if (entity.task?.user && entity.task.user && entity.task.user.name) {
          if (!devs.find(s => s.id === entity.task?.user?.id)) {
            devs.push({
              id: entity.task.user.id,
              name: entity.task.user.name,
            });
          }
        }

        if (entity.ticket?.ticket_tags?.length) {
          entity.ticket?.ticket_tags.forEach(tt => {
            if (tt!.tag && !tags.find(t => t.id === tt?.tag?.id)) {
              tags.push({
                id: tt!.tag!.id,
                name: tt!.tag!.name,
              });
            }
          });
        }
      }
    });

    if (!id) {
      numbers = this.sortPipe.transform(numbers, 'value');
      systems = this.sortPipe.transform(systems, 'description');
      status = this.sortPipe.transform(status, 'description');
      customers = this.sortPipe.transform(customers, 'name');
      devs = this.sortPipe.transform(devs, 'name');
      tags = this.sortPipe.transform(tags, 'name');
    }

    numberFilter!.options = numbers;
    systemFilter!.options = systems;
    statusFilter!.options = status;
    customersFilter!.options = customers;
    ticketDevFilter!.options = devs;
    tagsFilter!.options = tags;
  }

  filterTickets() {
    this.filteredTickets = [];
    let numberFilter = this.findOptions.filters?.find(f => f.name === 'number');
    let systemFilter = this.findOptions.filters?.find(f => f.name === 'system');
    let statusFilter = this.findOptions.filters?.find(f => f.name === 'status');
    let customersFilter = this.findOptions.filters?.find(f => f.name === 'customer');
    let ticketDevFilter = this.findOptions.filters?.find(f => f.name === 'ticketDev');
    let tagsFilter = this.findOptions.filters?.find(f => f.name === 'tags');

    for (let i = 0; i < this.ticketTasks.length; i++) {
      let ticketTask = this.ticketTasks[i];
      let ticket = ticketTask.ticket;
      let task = ticketTask.task;
      let numberOfFilters = 0;
      let inserts: boolean[] = [];

      let ticketDevValues = ticketDevFilter?.value;

      if (ticketDevValues?.length) {
        numberOfFilters++;
        //this.openDevsGroups(this.filter.dev); //À princípio não precisa

        for (let j = 0; j < ticketDevValues.length; j++) {
          let dev = ticketDevValues[j]?.id;

          if (ticket?.ticketUsers) {
            let td = ticket.ticketUsers.find(tu => tu.user_type === 'D');

            if ((td && td.user && td.user.id === dev) || (task?.user && task.user.id === dev)) {
              inserts.push(true);
              break;
            }
          }
        }
      }

      let numberValues = numberFilter?.value;

      if (numberValues?.length) {
        numberOfFilters++;

        for (let j = 0; j < numberValues.length; j++) {
          let number = numberValues[j]?.value;
          if (
            ticket &&
            ticket.ticket_number_teorema &&
            ticket.ticket_number_teorema!.indexOf(number) > -1
          ) {
            inserts.push(true);
            break;
          }
        }
      }

      let systemValues = systemFilter?.value;

      if (systemValues?.length) {
        numberOfFilters++;

        for (let j = 0; j < systemValues.length; j++) {
          let system = systemValues[j]?.id;

          if (
            (ticket && ticket.system && ticket.system!.id === system) ||
            (task?.systems?.length && task!.systems!.find(s => s.id === system))
          ) {
            inserts.push(true);
            break;
          }
        }
      }

      let statusValues = statusFilter?.value;

      if (statusValues?.length) {
        numberOfFilters++;

        for (let j = 0; j < statusValues.length; j++) {
          let status = statusValues[j]?.id;

          if (ticket && ticket.status && ticket.status!.id === status) {
            inserts.push(true);
            break;
          }
        }
      }

      let customersValues = customersFilter?.value;

      if (customersValues?.length) {
        numberOfFilters++;

        for (let j = 0; j < customersValues.length; j++) {
          let customer = customersValues[j]?.id;

          if (ticket && ticket.customer && ticket.customer!.id === customer) {
            inserts.push(true);
            break;
          }
        }
      }

      let tagsValues = tagsFilter?.value;

      if (tagsValues?.length) {
        numberOfFilters++;

        for (let j = 0; j < tagsValues.length; j++) {
          let tag = tagsValues[j]?.id;

          if (ticket?.ticket_tags?.length && ticket!.ticket_tags!.find(tt => tt.tag_id === tag)) {
            inserts.push(true);
            break;
          }
        }
      }

      let expectedDateValues = this.findOptions.filters?.find(
        f => f.name === 'expectedDate'
      )?.value;

      if (expectedDateValues?.length >= 2 && expectedDateValues[0] && expectedDateValues[1]) {
        let tempStartDate = new Date(expectedDateValues[0] + 'T00:00:00');
        let tempEndDate = new Date(expectedDateValues[1] + 'T00:00:00');

        numberOfFilters++;
        let expectedDate = this.tcTicketService.getExpectedDate(ticketTask);

        if (expectedDate) {
          if (
            expectedDate.getTime() >= tempStartDate.getTime() &&
            expectedDate.getTime() <= tempEndDate.getTime()
          ) {
            inserts.push(true);
          }
        } else {
          //Sem previsão
          inserts.push(true);
        }
      }

      if (inserts.length === numberOfFilters) {
        this.filteredTickets.push(ticketTask);
      }
    }

    // this.tcTicketService.saveFindOptionsStorage(this.findOptions);
    utils.saveFindOptionsStorage(this.findOptions);
    this.handleTickets();
    this.syncBoardState();
  }

  protected handleTickets(): any {
    console.log("this is an abstract function and should be implemented by it's sub components");
  }

  protected _handleTickets(noMatchLabel: string): any {
    this.sprints = {};
    this.delayedSprint = {};
    this.fillSprint(this.delayedSprint, noMatchLabel);

    this.notPunctuatedSprint = {};
    this.fillSprint(this.notPunctuatedSprint, noMatchLabel);
    this.punctuatedSprint = {};
    this.fillSprint(this.punctuatedSprint, noMatchLabel);
    this.delayedAndFinishedSprint = {};
    this.fillSprint(this.delayedAndFinishedSprint, noMatchLabel);

    let boards = {};
    let today = new Date();
    // let today = new Date(2022, 0, 1); console.log(today); //Para testes
    let nextDay = this.getNextDayOfWeek(new Date(today), 5);
    let nextSprint = `${nextDay.getFullYear()}-${nextDay.getMonth() + 1}-${nextDay.getDate()}`;
    boards[nextSprint] = [];

    this.filteredTickets.forEach(ticketTask => {
      /*if (ticketTask.ticket.ticketNumberTeorema === '0000436610') {
        debugger;
      }*/

      let task = ticketTask.task;
      let expectedDate = this.tcTicketService.getExpectedDate(ticketTask, true);

      if (expectedDate) {
        expectedDate = new Date(expectedDate.setHours(23, 59, 59));
        let isDelayed = expectedDate < today;

        if (isDelayed && task?.finished) {
          this.addTaskToSprint(this.delayedAndFinishedSprint, task, ticketTask);
        } else if (isDelayed) {
          this.addTaskToSprint(this.delayedSprint, task, ticketTask);
        } else {
          //nextDay = this.getNextDayOfWeek(expectedDate, 5); // pega a próxima sexta-feira
          nextDay = expectedDate;
          nextSprint = `${nextDay.getFullYear()}-${nextDay.getMonth() + 1}-${nextDay.getDate()}`;
          //let isDelayed = nextDay.getTime() < today.getTime();

          if (!boards[nextSprint]) {
            boards[nextSprint] = [];
          }

          boards[nextSprint].push(ticketTask);
        }
      } else if (ticketTask?.ticket?.estimated_effort) {
        this.addTaskToSprint(this.punctuatedSprint, task, ticketTask);
      } else {
        this.addTaskToSprint(this.notPunctuatedSprint, task, ticketTask);
      }
    });

    if (boards) {
      boards = this.adjustBoards(boards);
      for (let board in boards) {
        if (!this.sprints[board]) {
          this.sprints[board] = {};
        }

        this.fillSprint(this.sprints[board], noMatchLabel);

        boards[board].forEach(ticketTask => {
          let task = ticketTask.task;
          this.addTaskToSprint(this.sprints[board], task, ticketTask);
        });
      }
    }

    this.reorderDelayedTicketTasks();
  }

  reorderDelayedTicketTasks() {
    if (this.delayedSprint) {
      for (let user in this.delayedSprint) {
        let ticketTasks = this.delayedSprint[user].ticketTasks;

        if (ticketTasks && ticketTasks.length) {
          ticketTasks.sort((a: any, b: any) => {
            let aExpectedDate = this.tcTicketService.getExpectedDate(a);
            let bExpectedDate = this.tcTicketService.getExpectedDate(b);

            if (aExpectedDate < bExpectedDate) {
              return -1;
            } else if (aExpectedDate > bExpectedDate) {
              return 1;
            } else {
              return 0;
            }
          });

          ticketTasks = this.putDateBadges(ticketTasks);
          this.delayedSprint[user].ticketTasks = ticketTasks;
        }
      }
    }
  }

  fillSprint(sprint, key) {
    if (!sprint[key]) {
      sprint[key] = {};
    }
    if (!sprint[key].estimatedEffort) {
      sprint[key].estimatedEffort = 0;
    }
    if (!sprint[key].finishedPoints) {
      sprint[key].finishedPoints = 0;
    }
    if (!sprint[key].ticketTasks) {
      sprint[key].ticketTasks = [];
    }
  }

  addTaskToSprint(sprint, task, ticketTask) {}

  _addTaskToSprint(sprint, task, ticketTask, key, noMatchLabel) {
    const sprintKey = key || noMatchLabel;

    if (key) {
      this.fillSprint(sprint, key);
    }

    const targetSprint = sprint[sprintKey];

    if (targetSprint) {
      targetSprint.estimatedEffort += task.estimated_effort;

      if (task.finished) {
        targetSprint.finishedPoints += task.estimated_effort;
      }

      if (targetSprint && !targetSprint.hasUrgent) {
        targetSprint.hasUrgent = !!ticketTask.ticket.urgent;
      }

      if (targetSprint && !targetSprint.hasFailedTests) {
        const status = ticketTask.ticket.status;
        targetSprint.hasFailedTests = status?.description === 'Teste não ok';
      }

      if (targetSprint && !targetSprint.hasBug) {
        const type = ticketTask.ticket.ticket_type;
        targetSprint.hasBug =
          type?.description == 'Chamada Tipo Problema' ||
          type?.description == 'Chamada Defeito / Retrabalho';
      }

      if (targetSprint && !targetSprint.hasPendingRelease) {
        const type = ticketTask.ticket.type;
        const release_situation = ticketTask.ticket.release_situation;
        targetSprint.hasPendingRelease = type == 'L' || release_situation == 'P';
      }

      targetSprint.ticketTasks.push(ticketTask);
    }
  }

  getSprintByTitle(title: string, isDrop: boolean) {
    let handleInvalidDrop = () => {
      this.toastService.show({
        color: 'warn',
        message: 'Não é possível arrastar para a coluna ' + title,
      });
      return null;
    };

    switch (title) {
      case 'Não pontuados':
        return isDrop ? handleInvalidDrop() : this.notPunctuatedSprint;
      case 'Já pontuados':
        return isDrop ? handleInvalidDrop() : this.punctuatedSprint;
      case 'Concluídos':
        return isDrop ? handleInvalidDrop() : this.delayedAndFinishedSprint;
      case 'Atrasados':
        return this.delayedSprint;
      default:
        return this.sprints[title];
    }
  }

  minimizeSprint(e) {
    this.minimizedSprints = {
      ...this.minimizedSprints,
      [e.title]: e.color,
    };
    this.saveMinimizedSprints();
    this.reloadMinimizedSprints();
  }

  unminimizeSprint(title: string) {
    const { [title]: removed, ...rest } = this.minimizedSprints;
    this.minimizedSprints = rest;
    this.saveMinimizedSprints();
    this.reloadMinimizedSprints();
  }

  reloadMinimizedSprints() {
    let temp = this.minimizedSprints;
    this.minimizedSprints = {};
    this.patchState({ minimizedSprints: {} });

    setTimeout(() => {
      this.minimizedSprints = temp;
      this.patchState({ minimizedSprints: { ...this.minimizedSprints } });
    }, 1);
  }

  getMinimizedSprintKeys(source: Record<string, any> = this.minimizedSprints) {
    return Object.keys(source || {});
  }

  saveMinimizedSprints() {
    localStorage.setItem('minimizedSprints', JSON.stringify(this.minimizedSprints));
  }

  loadMinimizedSprints() {
    let temp = localStorage.getItem('minimizedSprints');

    if (temp) {
      try {
        this.minimizedSprints = JSON.parse(temp);
      } catch (e) {
        console.error(e);
      }
    }

    this.patchState({ minimizedSprints: { ...this.minimizedSprints } });
  }

  getObjectDateKeys(object): string[] {
    return Object.keys(object).sort((a, b) => {
      let dateA = new Date(a);
      let dateB = new Date(b);
      if (dateA > dateB) return 1;
      if (dateA < dateB) return -1;
      return 0;
    });
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    this.screenHeight = window.innerHeight;
  }

  existTickets(object) {
    for (let i in object) {
      if (object[i]['ticketTasks'] && object[i]['ticketTasks'].length) {
        return true;
      }
    }
    return false;
  }

  openTicketModal(ticketTask: TicketTask) {
    if (this.openingTicketModal) return;

    this.openingTicketModal = true;
    this.showCurrentTicketModal = false;
    this.currentTicket = ticketTask.ticket;
    this.currentTaskId = ticketTask?.task?.id;

    setTimeout(() => {
      this.zone.run(() => {
        this.showCurrentTicketModal = true;
        this.openingTicketModal = false;
        this.changeDetector.markForCheck();
      });
    }, 1);
  }

  filterChanged(event: any) {
    if (
      (this.responsibleFilterIndex !== undefined &&
        this.responsibleFilterIndex > -1 &&
        event?.index === this.responsibleFilterIndex) ||
      (this.situationFilterIndex !== undefined &&
        this.situationFilterIndex > -1 &&
        event?.index === this.situationFilterIndex)
    ) {
      this.filterUserChanged = true;
    }
  }

  get responsibleFilterIndex() {
    return this.findOptions.filters?.findIndex(f => f.name === 'responsible');
  }

  get situationFilterIndex() {
    return this.findOptions.filters?.findIndex(f => f.name === 'situation');
  }

  filterButtonClicked() {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTimeout(() => {
      this.zone.run(() => {
        if (this.filterUserChanged || this.hasServerFiltersChanged()) {
          this.getTickets();
        } else {
          this.filterTickets();
        }
        this.changeDetector.markForCheck();
      });
    }, 200);
  }

  hasServerFiltersChanged() {
    let currentResponsible = this.findOptions.filters?.find(f => f.name === 'responsible')?.value;
    let currentSituation = this.findOptions.filters?.find(f => f.name === 'situation')?.value;

    let respChanged =
      JSON.stringify(currentResponsible) !== JSON.stringify(this.lastFilteredResponsible);
    let sitChanged =
      JSON.stringify(currentSituation) !== JSON.stringify(this.lastFilteredSituation);

    return respChanged || sitChanged;
  }

  ngOnDestroy(): void {
    this.layoutService.resetPadding('bottom');
    //this.layoutService.resetDimensions('height');
    this.layoutService.resetBlockContentStyle('overflow-x');

    this.subscriptions.forEach(s => {
      s.unsubscribe();
    });

    this.sprintStateService.reset();
  }

  ticketModalClosed(context: any) {
    this.showCurrentTicketModal = false;

    if (context.saved) {
      this.setLoading(true);

      setTimeout(() => {
        try {
          let newTicket: Ticket = context.entity;
          let index = this.tickets.findIndex(t => t.id === newTicket.id);

          if (index > -1) {
            let remove = false;

            if (newTicket.situation != 'A') {
              remove = true;
            }

            if (!remove) {
              let userFilter = newTicket.ticketUsers?.find(tu => tu.user_type == 'R');

              if (userFilter) {
                let resp = userFilter.user;
                let users = this.findOptions.filters?.find(f => f.name === 'responsible')?.value;

                if (!users.find((u: User) => u.id === resp?.id.toString())) {
                  remove = true;
                }
              }
            }

            if (remove) {
              this.tickets.splice(index, 1);
            } else {
              // Normaliza ticket_tags para o formato esperado pelos cards da sprint
              const ticketAny: any = newTicket as any;
              const tags = ticketAny?.ticket_tags;

              if (Array.isArray(tags) && tags.length) {
                const first = tags[0];

                if (typeof first === 'string') {
                  ticketAny.ticket_tags = tags.map((name: string) => ({
                    tag: { name },
                  }));
                }
              }

              this.tickets[index] = newTicket;
            }

            this.updateTasksByTicket(newTicket, true);
            this.adjustTicketUsersList(newTicket.id);
            this.filterTickets();
          }
        } finally {
          this.setLoading(false);
        }
      }, 1);
    }
  }

  updateTasksByTicket(ticket: Ticket, refresh: boolean) {
    if (refresh) {
      let newTicketTasks: TicketTask[] = [];

      for (let i = 0; i < this.ticketTasks.length; i++) {
        if (this.ticketTasks[i].ticket?.id !== ticket.id) {
          newTicketTasks.push(this.ticketTasks[i]);
        }
      }

      this.ticketTasks = newTicketTasks;
      this.populateTasks();

      this.updateTicketLogsByTicket(ticket);
    } else {
      this.ticketTasks.forEach(ticketTask => {
        if (ticketTask.ticket?.id === ticket.id) {
          ticketTask.ticket = ticket;
        }
      });

      this.findTicketLogs(ticket.id);
    }
  }

  updateTicketLogsByTicket(ticket: Ticket) {
    if (ticket && ticket?.ticket_logs) {
      ticket?.ticket_logs?.forEach(log => {
        if (log.user?.name) {
          this.ticketTasks.forEach(ticketTask => {
            if (ticketTask.ticket?.id == log.ticket_id && !log.end_date) {
              if (log.task && log.task.id === ticketTask.task?.id) {
                this.addUserLogToTicket(ticketTask.task, log.user!.name!);
              }

              this.addUserLogToTicket(ticketTask.ticket, log.user!.name!);
            }
          });
        }
      });
    }
  }

  async findTicketLogs(id?) {
    if (this.logsLoaded || this.isAgentTicket) return;

    let ids: string[] = [];

    if (id) {
      ids = [id];
    } else {
      this.tickets.map(ticket => {
        ids.push(ticket.id);
      });
    }

    let response = await this.tcTicketLogService.findAllByTicketIds(ids, false).catch(e => {
      console.error(e);
      this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
    });

    if (response?.data) {
      response.data.forEach(log => {
        if (log.user?.name) {
          this.ticketTasks.forEach(ticketTask => {
            if (ticketTask.ticket?.id == log.ticket_id) {
              if (log.task && log.task.id === ticketTask.task?.id) {
                this.addUserLogToTicket(ticketTask.task, log.user!.name!);
              }

              this.addUserLogToTicket(ticketTask.ticket, log.user!.name!);
            }
          });
        }
      });

      if (!id) {
        this.logsLoaded = true;
      }
    }
  }

  addUserLogToTicket(ticketOrTask, userName: string) {
    if (!ticketOrTask.userLog || !ticketOrTask.userLog.includes(userName)) {
      if (ticketOrTask.userLog && ticketOrTask.userLog.length > 0) {
        ticketOrTask.userLog += ', ';
      } else {
        ticketOrTask.userLog = '';
      }

      ticketOrTask.userLog += userName;
    }

    return ticketOrTask;
  }

  removeUserLogFromTicket(ticketOrTask, userName: string) {
    if (ticketOrTask.userLog && ticketOrTask.userLog.includes(userName)) {
      ticketOrTask.userLog = ticketOrTask.userLog
        .replace(userName, '')
        .replace(', , ', ', ')
        .trim();

      if (ticketOrTask.userLog.startsWith(', ')) {
        ticketOrTask.userLog = ticketOrTask.userLog.substring(2);
      }

      if (ticketOrTask.userLog.endsWith(',')) {
        ticketOrTask.userLog = ticketOrTask.userLog.substring(0, ticketOrTask.userLog.length - 1);
      }
    }

    return ticketOrTask;
  }

  saveResponsible(users) {
    if (users) {
      localStorage.setItem('responsibles', JSON.stringify(users));
    }
  }

  updateNotificationTitleOnInit() {
    const notifications = this.ticketNotificationsService.getTicketNotifications();
    this.ticketNotificationsService.updatePageTitle(notifications.length);
  }

  /**
   * Processa mensagens recebidas no canal pessoal UUID do usuário.
   * Cria notificações push no localStorage para exibição no modal de notificações.
   */
  async processUserChannelNotification(payload: any): Promise<void> {
    try {
      const user = utils.getUser();
      if (!user) return;

      const bwUserCG = utils.getBwUserCompanyGroup(user);
      const correctUserId = bwUserCG?.user_id || user?.id;

      const ticketRef = payload.ticket_number
        ? String(payload.ticket_number).padStart(10, '0')
        : payload.ticket_id;

      const changedBy = payload.changed_by_user_name || 'Usuário';
      const changes = payload.changes || 'Chamado atualizado';

      const notification = {
        ref: ticketRef,
        uuid: payload.ticket_id,
        title: `Alterado por ${changedBy}`,
        summary: changes,
        type: 'ticket' as const,
        userId: correctUserId,
        icon: payload.urgent ? 'bi-exclamation-triangle-fill' : 'bi-ticket',
        iconColor: payload.urgent ? 'text-danger' : 'text-primary',
        iconTooltip: payload.urgent ? 'Chamado Urgente' : 'Chamado',
      };

      this.ticketNotificationsService.addTicketNotification(notification);
    } catch (error) {
      console.error('Erro ao processar notificação do canal pessoal:', error);
    }
  }

  async checkAndNotifyUser(
    updatedTicket: any,
    description: string,
    changedByUserId?: string
  ): Promise<void> {
    try {
      const currentUser = await this.userService.getCurrentUser().catch(() => null);
      if (!currentUser?.id) {
        return;
      }

      if (changedByUserId && changedByUserId === currentUser.id) {
        return;
      }

      if (this.ticketNotificationsService.shouldNotifyUser(updatedTicket, currentUser.id)) {
        const notification = this.ticketNotificationsService.createNotificationFromTicket(
          updatedTicket,
          currentUser.id,
          description
        );

        if (notification) {
          this.ticketNotificationsService.addTicketNotification(notification);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar notificação:', error);
    }
  }
  // adicionando métodos comuns entre a sprint e o kanban para não repetir

  adjustBoards(boards) {
    //Cria boards faltantes (em branco)
    /*for (let board in boards) {
      let aux = new Date(board)
      let date = new Date(aux.setDate(aux.getDate() + 1))
      let nextDay = this.getNextDayOfWeek(date, 5)
      let nextSprint = `${nextDay.getFullYear()}-${nextDay.getMonth() + 1}-${nextDay.getDate()}`

      if (!boards[nextSprint]) {
        if (this.existNextSprint(boards, nextSprint)) {
          boards[nextSprint] = []
          return this.adjustBoards(boards)
        }
      }
    }*/

    let sprints = Object.keys(boards).sort((a, b) => {
      if (new Date(a) < new Date(b)) return 1;
      if (new Date(a) > new Date(b)) return -1;
      return 0;
    });

    let lastSprint = sprints[0];
    let aux = new Date(lastSprint);
    let date = new Date(aux.setDate(aux.getDate() + 1));
    let nextDay = this.getNextDayOfWeek(date, 5);
    let nextSprint = `${nextDay.getFullYear()}-${nextDay.getMonth() + 1}-${nextDay.getDate()}`;

    if (!boards[nextSprint]) {
      boards[nextSprint] = [];
    }

    return boards;
  }

  getNextDayOfWeek(date, dayOfWeek) {
    let resultDate = new Date(date.getTime());
    resultDate.setDate(date.getDate() + ((7 + dayOfWeek - date.getDay()) % 7));
    resultDate.setHours(23, 59, 59);
    return resultDate;
  }

  putDateBadges(ticketTasks) {
    let newList: any[] = [];
    let lastExpectedDate;

    ticketTasks.forEach(ticketTask => {
      let expectedDate = this.tcTicketService.getExpectedDate(ticketTask);

      if (expectedDate) {
        expectedDate = this.datePipe.transform(expectedDate, 'dd/MM/yyyy');

        if (expectedDate !== lastExpectedDate) {
          newList.push({
            badge: true,
            title: expectedDate,
            ticketTasks: [],
          });

          lastExpectedDate = expectedDate;
        }

        let badge = newList.find(t => t.badge && t.title === expectedDate);

        if (badge) {
          badge.ticketTasks.push(ticketTask);
        }
      } else {
        newList.push(ticketTask);
      }
    });

    return newList;
  }
}
